/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'underscore'
], function(
	_
) {
	class QuerySelectionBuilder {
		constructor() {}

		build(dataItem, filtersList=[], searchFilters) {
			const finalSelections = [];
			//By default, return selections in the order agreed to by PM.
			finalSelections.push(...this._buildSelectionsFromDrillStateForHierarchyOrSet(dataItem, filtersList, searchFilters));
			finalSelections.push(...this._buildSelectionsFromFiltersForOLAPDataItems(dataItem, filtersList));
			finalSelections.push(...this._buildSelectionsForTopBottom(dataItem));
			finalSelections.push(...this._buildSelectionsForSort(dataItem));
			return finalSelections;
		}

		//For hierarchies or OLAP set, build one of root members, children of single root or children of current drill member.	
		_buildSelectionsFromDrillStateForHierarchyOrSet(dataItem, filterList, searchFilters=[]) {
			const selections=[];
			// Do not convert to hierarchy to level set if has search filters
			if (searchFilters.find(searchFilter => searchFilter.columnId === dataItem.getColumnId())) {
				return selections;
			}

			const matchingLocalFilterEntry = _.find(filterList, filter=>filter.columnId === dataItem.getColumnId());
			const overrideHierarchyDefaultSelection = matchingLocalFilterEntry && matchingLocalFilterEntry.overrideHierarchyDefaultSelection;

			const metaDataColumn = dataItem.getMetadataColumn();
			if (metaDataColumn.isHierarchy() || metaDataColumn.isNamedSet()) {
				const isolatedValue = dataItem.getIsolatedValue();
				const drillDownValue = dataItem.getDrillDownValue();
				const drillUpValue = dataItem.getDrillUpValue();
				if (isolatedValue || drillDownValue || drillUpValue) {
					if (isolatedValue) {
						selections.push({
							'operation': 'keep',
							'set': [isolatedValue]
						});
					} else if (drillDownValue) {
						selections.push({
							'operation': 'add',
							'children': drillDownValue
						});
					} else if (drillUpValue) {
						selections.push({
							'operation': 'add',
							'drillUp':drillUpValue
						});
					}
				} else if (metaDataColumn.isHierarchy() && !overrideHierarchyDefaultSelection) {
					// default behavior.	
					if (metaDataColumn.isSingleRootHierarchy()) {
						selections.push({
							'operation': 'add',
							'children': metaDataColumn.getRootMember()
						});
					} else {
						selections.push({
							'operation': 'add',
							'rootMembers': true
						});
					}
				}
			}
			return selections;
		}

		_buildSelectionsForTopBottom(dataItem) {
			const topBottom = dataItem.getTopBottom();
			if (!topBottom) {
				return [];
			}

			const topBottomQuerySpec = {
				'operation':'keep',
				'topBottom': {
					'type':topBottom.type,
					'value':topBottom.value
				}
			};
			if (topBottom.context) {
				topBottomQuerySpec.context = [ topBottom.context ];
			}
			if (topBottom.rank) {
				topBottomQuerySpec.topBottom.rank = topBottom.rank;
			}
			return [topBottomQuerySpec];

		}

		_convertFilterEntryToSelection(filterEntry) {
			let discreteOperation = this._convertFilterOperatorToSetOperation(filterEntry.operator);
			let result = null;
			if (discreteOperation) {
				let valuesArray = filterEntry.values;
				result = {
					operation: discreteOperation
				};

				if (filterEntry.operator === 'containsignorecase') {
					result.filter = {
						operator: filterEntry.operator,
						values: valuesArray.map(value=>value.u)
					};
				} else {
					result.set = valuesArray.map(value=>value.u);
				}
			}
			return result;
		}

		_convertFilterOperatorToSetOperation(operator) {
			switch (operator) {
				case 'in':
				case 'containsignorecase':
					return 'keep';
				case 'notin':
					return 'remove';
				default:
					return;
			}
		}

		_buildSelectionsFromFiltersForOLAPDataItems(dataItem, filterList, outputEdgeFilterExceptions) {
			const metadataColumn = dataItem.getMetadataColumn();
			if (!metadataColumn.isOlapColumn() || metadataColumn.isProperty() ) {
				return [];
			}
			const dataItemLocalFilters = filterList.filter(filter=>filter.columnId === dataItem.getColumnId());

			const selections = [];
			dataItemLocalFilters.forEach(filterEntry=>{
				if (filterEntry && filterEntry.values.length > 0 && !filterEntry.conditions) {
					let selectionItem = this._convertFilterEntryToSelection(filterEntry);
					if (selectionItem) {
						selections.push(selectionItem);
					}
				}
			});
			if (selections.length > 0) {
				//Track which filters were converted to dataItemSelections (so we don't filter on them as well!)
				if (outputEdgeFilterExceptions) {
					outputEdgeFilterExceptions.push(dataItem.getColumnId());
				}
			}
			return selections;
		}

		/**
		 * @todo: should figure out how to do the sort selection here.
		 * this is a temporary code
		 */
		_buildSelectionsForSort(dataItem) {
			const sortInfo = dataItem.getSort();
			return sortInfo ? [{
				'operation': 'order',
				'sort': _.extend({}, _.pick(sortInfo, 'type', 'by', 'priority'))
			}] : [];
		}

	}

	return QuerySelectionBuilder;
});
